import { useTranslation } from 'react-i18next';
import {
  getModelFamilyName,
  getPaywallComparisonRows,
  PAYWALL_DETAIL_BODY_KEYS,
  PAYWALL_TOOL_GROUPS,
  PAYWALL_TOOLS_NOTE_KEY,
  PLAN_PRESENTATION,
  STACKED_LOGO_NAMES,
  type ModelFamily,
  type PaidJourneyTier,
  type PaywallDetailKey,
  type StackedLogoId,
} from '../../lib/subscription-journey/plan-presentation';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { STACKED_LOGO_SVG, TOOL_ICON_SVG } from './subscription-brand-marks';

const TIER_NAMES: Record<PaidJourneyTier, string> = { plus: 'Plus', pro: 'Pro' };
/** The model family's mark in the shared brand set (OpenAI ships as the ChatGPT mark). */
const MODEL_MARK: Record<ModelFamily, StackedLogoId> = { gemini: 'gemini', claude: 'claude', openai: 'chatgpt' };

export type PaywallDetailOpenState = Record<PaywallDetailKey, boolean>;

/**
 * B: the Free-versus-selected-plan table (`.premium-comparison` in the
 * design). Rows come from `getPaywallComparisonRows`; the three detail rows
 * are `<details>` whose open state the paywall owns, so it survives switching
 * plans. Availability marks carry screen-reader text; the paid column's ticks
 * are brand-coloured and the CV writing row shows the tier's model mark.
 */
export function PaywallComparison({
  tier,
  openDetails,
  onToggleDetail,
}: {
  tier: PaidJourneyTier;
  openDetails: PaywallDetailOpenState;
  onToggleDetail: (detail: PaywallDetailKey, open: boolean) => void;
}) {
  const { t } = useTranslation('common');
  const tierName = TIER_NAMES[tier];
  const model = PLAN_PRESENTATION[tier].model;
  const rows = getPaywallComparisonRows(tier);

  const availability = (included: boolean, paid: boolean) => (
    <span
      className={`tabbio-sub-premium-availability${included ? ' is-included' : ''}${paid ? ' is-paid' : ''}`}
    >
      <span className="tabbio-sub-premium-sr">
        {included ? t('subscription.paywall.included') : t('subscription.paywall.notIncluded')}
      </span>
      {included ? <MaterialSymbol name="check" /> : <span aria-hidden>–</span>}
    </span>
  );

  const toolIcon = (id: StackedLogoId) => (
    <span
      className="tabbio-sub-premium-tool-icon"
      aria-hidden
      dangerouslySetInnerHTML={{ __html: TOOL_ICON_SVG[id] }}
    />
  );

  const detailBody = (detail: PaywallDetailKey) => {
    if (detail === 'tools') {
      return (
        <>
          {PAYWALL_TOOL_GROUPS.map((group) => (
            <div key={group.key}>
              <p>{t(group.labelKey)}</p>
              <ul className="tabbio-sub-premium-tools">
                {group.logos.map((id) => (
                  <li key={id}>
                    {toolIcon(id)}
                    <span>{STACKED_LOGO_NAMES[id]}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <p>{t(PAYWALL_TOOLS_NOTE_KEY)}</p>
        </>
      );
    }
    return <p>{t(PAYWALL_DETAIL_BODY_KEYS[detail])}</p>;
  };

  return (
    <div className="tabbio-sub-premium-comparison">
      <table>
        <caption className="tabbio-sub-premium-sr">
          {t('subscription.paywall.compareCaption', { tier: tierName })}
        </caption>
        <thead>
          <tr>
            <th scope="col">{t('subscription.paywall.columns.features')}</th>
            <th scope="col">{t('subscription.paywall.columns.free')}</th>
            <th scope="col">{tierName}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const detail = row.kind === 'detail' ? row.detail : undefined;
            return (
              <tr key={row.key} className={detail ? 'is-detail' : undefined}>
                <th scope="row">
                  {detail ? (
                    <details
                      className="tabbio-sub-premium-detail"
                      data-feature={detail}
                      open={openDetails[detail]}
                      onToggle={(event) =>
                        onToggleDetail(detail, (event.currentTarget as HTMLDetailsElement).open)
                      }
                    >
                      <summary>{t(row.labelKey)}</summary>
                      <div className="tabbio-sub-premium-detail-body">{detailBody(detail)}</div>
                    </details>
                  ) : (
                    t(row.labelKey)
                  )}
                </th>
                <td>{availability(row.free, false)}</td>
                <td>
                  {row.kind === 'model' && model ? (
                    <span
                      className="tabbio-sub-premium-model"
                      role="img"
                      aria-label={getModelFamilyName(model)}
                      title={getModelFamilyName(model)}
                      dangerouslySetInnerHTML={{ __html: STACKED_LOGO_SVG[MODEL_MARK[model]] }}
                    />
                  ) : (
                    availability(true, true)
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
